import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { SectionLiveKpi } from '@/types';
import type { BarnLiveKpi, PigTallyDay, Productivity, ProductivityComparison, WaterNormDay, WeightSeries } from '@/types/SectionLiveKpi';

// An inclusive [from, to] of ISO calendar dates (yyyy-MM-dd), as every ranged read on this
// controller takes them. One type, aliased below where a caller wants a domain name for it: two
// declarations of the same members would be two names for one idea and two places to change it.
export type DateRange = {
    from: string;
    to: string;
};

export type PigTallyRange = DateRange;
export type ProductivityRange = DateRange;

export type ProductivityGranularity = 'hour' | 'day';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 30, // 30 seconds - this is a "live" surface
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // poll every 5 minutes while the page is mounted
};

export const queryKeys = {
    getSectionLiveKpisRoot: ['active-groups', 'live-kpis'],
    getSectionLiveKpis: (sectionIds: Ref<number[]>) => [...queryKeys.getSectionLiveKpisRoot, sectionIds],
    getBarnLiveKpisRoot: ['active-groups', 'barn-kpis'],
    getBarnLiveKpis: (barnIds: Ref<number[]>) => [...queryKeys.getBarnLiveKpisRoot, barnIds],
    getPigTallySeriesRoot: ['active-groups', 'pig-tally'],
    getPigTallySeries: (sectionId: Ref<number | null>, range?: Ref<PigTallyRange | null>) => [...queryKeys.getPigTallySeriesRoot, sectionId, range],
    getWaterNormSeriesRoot: ['active-groups', 'water-norm'],
    getWaterNormSeries: (sectionId: Ref<number | null>) => [...queryKeys.getWaterNormSeriesRoot, sectionId],
    getWeightSeriesRoot: ['active-groups', 'weight-series'],
    getWeightSeries: (sectionId: Ref<number | null>, hourly: Ref<boolean>) => [...queryKeys.getWeightSeriesRoot, sectionId, hourly],
    getProductivityRoot: ['active-groups', 'productivity'],
    getProductivity: (
        sectionId: Ref<number | null>,
        range: Ref<ProductivityRange | null>,
        granularity: Ref<ProductivityGranularity>
    ) => [...queryKeys.getProductivityRoot, sectionId, range, granularity],
    // A sibling of getProductivityRoot, deliberately not a segment beneath it. Vue Query
    // matches keys by prefix, so nesting this under the main series' root would make any
    // invalidation of the main series evict the comparison cache with it - the long gcTime below
    // exists precisely so a reference stays warm across those refetches.
    getProductivityComparisonRoot: ['active-groups', 'productivity-comparison'],
    // Keyed by granularity as well as by period, because the two grains are two different answers
    // rather than one answer fetched twice: a day request carries the per-pig water reference and an
    // hour request omits it (see useProductivityComparison).
    getProductivityComparison: (productionGroupId: Ref<number | null>, granularity: Ref<ProductivityGranularity>) => [...queryKeys.getProductivityComparisonRoot, productionGroupId, granularity],
};

// Single batch call for every section on the page, addressed by section
// rather than by barn. The frontend is the source of truth for which sections are in scope — it
// already holds them, filtered by farm and PigType, to build the rows this response is merged into —
// so section_ids are passed directly rather than barn ids the server would resolve back into
// sections. Addressing the resource by its own identity also lets one section's card be refreshed
// on its own. Still one round-trip regardless of how many farms are selected. Cache is invalidated
// at the root key so alarm form completions refresh all sections.
export function useSectionLiveKpis(sectionIds: Ref<number[]>, options: UseQueryOptions<SectionLiveKpi[]> = {}) {
    return useQuery<SectionLiveKpi[]>({
        queryKey: queryKeys.getSectionLiveKpis(sectionIds),
        queryFn: () => {
            const ids = sectionIds.value;
            if (ids.length === 0) return Promise.resolve([]);
            return ApiClient.send('get', `monitor/production-groups/active?section_ids=${ids.join(',')}`, {});
        },
        enabled: computed(() => sectionIds.value.length > 0),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

// On-demand day-by-day pig tally for one Section. The legacy caller omits `range` and receives the
// active Production Group-oriented history; setup supplies a range so it can inspect a Section before a
// Production Group exists. Both remain on-demand rather than part of the polled page payload.
export function usePigTallySeries(sectionId: Ref<number | null>, range?: Ref<PigTallyRange | null>) {
    return useQuery<PigTallyDay[]>({
        queryKey: queryKeys.getPigTallySeries(sectionId, range),
        queryFn: () => {
            const selectedRange = range?.value;
            const dateRange = selectedRange ? `&from=${selectedRange.from}&to=${selectedRange.to}` : '';
            return ApiClient.send('get', `monitor/production-groups/pig-tally?section_id=${sectionId.value}${dateRange}`, {});
        },
        enabled: computed(() => sectionId.value !== null && (!range || range.value !== null)),
        staleTime: 1000 * 60, // registrations change rarely within a modal-viewing session
        gcTime: 1000 * 60 * 5,
    });
}

// On-demand day-by-day water-norm series for one section's current batch — fetched only while the
// water detail modal is open (sectionId != null), never as part of the polled page load. Mirrors
// usePigTallySeries exactly (same guard, same query-key shape).
export function useWaterNormSeries(sectionId: Ref<number | null>) {
    return useQuery<WaterNormDay[]>({
        queryKey: queryKeys.getWaterNormSeries(sectionId),
        queryFn: () => ApiClient.send('get', `monitor/production-groups/water-norm?section_id=${sectionId.value}`, {}),
        enabled: computed(() => sectionId.value !== null),
        staleTime: 1000 * 60, // readings change rarely within a modal-viewing session
        gcTime: 1000 * 60 * 5,
    });
}

// On-demand day-by-day (or hour-by-hour) weight/growth series for one section's current batch —
// fetched only while the weight detail modal is open (sectionId != null), never as part of the
// polled page load. Mirrors usePigTallySeries/useWaterNormSeries, with an extra `hourly` ref (the
// granularity toggle) folded into the query key so switching it re-triggers the fetch.
//
// Answers with the Section-wide aggregate and every contributing camera's own series in one read —
// the camera set belongs to the same window as the buckets, so splitting it across two requests
// would let the two disagree about which cameras a figure rests on.
export function useWeightSeries(sectionId: Ref<number | null>, hourly: Ref<boolean>) {
    return useQuery<WeightSeries>({
        queryKey: queryKeys.getWeightSeries(sectionId, hourly),
        queryFn: () => ApiClient.send('get', `monitor/production-groups/weight-series?section_id=${sectionId.value}&hourly=${hourly.value}`, {}),
        enabled: computed(() => sectionId.value !== null),
        staleTime: 1000 * 60, // readings change rarely within a modal-viewing session
        gcTime: 1000 * 60 * 5,
    });
}

// On-demand Productivity Chart for one section over an explicit date range and granularity —
// every Production Group overlapping the range (resolved server-side) plus the per-bucket weight
// buckets spanning them. Fetched only while the detail overlay is open (sectionId != null) and a
// range has been resolved (see the overlay's default-range logic). `granularity` is folded into
// the query key, same as `useWeightSeries`'s `hourly`, so switching it re-triggers the fetch.
export function useProductivity(
    sectionId: Ref<number | null>,
    range: Ref<ProductivityRange | null>,
    granularity: Ref<ProductivityGranularity>
) {
    return useQuery<Productivity>({
        queryKey: queryKeys.getProductivity(sectionId, range, granularity),
        queryFn: () => {
            const selectedRange = range.value as ProductivityRange;
            return ApiClient.send(
                'get',
                `monitor/production-groups/productivity?section_id=${sectionId.value}&from=${selectedRange.from}&to=${selectedRange.to}&granularity=${granularity.value}`,
                {}
            );
        },
        enabled: computed(() => sectionId.value !== null && range.value !== null),
        // Hold the previous render while a new range or granularity loads. Without it every flip
        // drops the whole chart to a spinner and back, which reads as a reload rather than as a
        // change of scale - and undoes the point of keeping every variant cached below.
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60, // readings change rarely within an overlay-viewing session
        gcTime: 1000 * 60 * 5,
    });
}

// The comparison reference read for a single Production Group, fetched only once one is picked from
// the comparison dropdown (productionGroupId != null) - opening the chart never fetches a comparison
// nobody asked for.
//
// The chart's own granularity is forwarded as it stands. The reference's buckets are keyed by
// day-in-period whichever grain is asked for, but its *fields* are not: a per-pig water figure has
// no hourly form, so the server states a water reference for a day request and omits it for an hour
// one. Asking at the grain the chart is drawn at is therefore what makes the reference comparable
// with the series it is drawn against, and is why the two grains are cached separately. The long
// gcTime keeps a picked reference warm for the rest of the overlay-viewing session without a bespoke
// cache of our own.
export function useProductivityComparison(productionGroupId: Ref<number | null>, granularity: Ref<ProductivityGranularity>) {
    return useQuery<ProductivityComparison>({
        queryKey: queryKeys.getProductivityComparison(productionGroupId, granularity),
        queryFn: () => ApiClient.send('get', `monitor/production-groups/productivity/comparison?production_group_id=${productionGroupId.value}&granularity=${granularity.value}`, {}),
        enabled: computed(() => productionGroupId.value !== null),
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60 * 5, // a closed Production Group's history never changes within a session
        gcTime: 1000 * 60 * 30, // stays warm for the rest of the overlay-viewing session
    });
}

// Barn-level aggregate KPIs: pig count (every section in the barn) and dead count, carried as both
// the 7-day rolling and since-Production Group-start cumulative windows so the page-level mortality view
// toggle can switch which one is shown.
export function useBarnLiveKpis(barnIds: Ref<number[]>) {
    return useQuery<BarnLiveKpi[]>({
        queryKey: queryKeys.getBarnLiveKpis(barnIds),
        queryFn: () => {
            const ids = barnIds.value;
            if (ids.length === 0) return Promise.resolve([]);
            return ApiClient.send('get', `monitor/production-groups/barn-kpis?barn_ids=${ids.join(',')}`, {});
        },
        enabled: computed(() => barnIds.value.length > 0),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
    });
}
